import { useEffect, useMemo, useState } from 'react';
import { getMonthGrid } from '../lib/calendarGrid';
import { usePhotoPreviewUrls } from '../lib/usePhotoPreviewUrls';
import { loadLastProject, saveProject } from '../lib/projectStorage';
import type { CalendarPage, CalendarPhoto, CalendarProject } from '../types/calendarProject';
import { Button } from '../components/ui/Button';
import { Logo } from '../components/ui/Logo';

const progressSteps = ['Produkt', 'Fotky', 'Návrh', 'Editor', 'Preflight'];
const productLabels = { basic_year_story: 'Basic Year Story', family_planner_cz: 'Family Planner CZ', fineart_gallery: 'FineArt Gallery' };
const statusLabels = { not_checked: 'Nezkontrolováno', ok: 'OK', warning: 'Warning', error: 'Error' };
const statusClassNames = { not_checked: 'is-muted', ok: 'is-ok', warning: 'is-warning', error: 'is-error' };
const weekDays = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
const layoutOptions = [
  { id: 'large-photo-clean', title: 'Large Photo Clean' },
  { id: 'photo-top-calendar-bottom', title: 'Photo top / calendar bottom' },
  { id: 'full-bleed-photo', title: 'Full-bleed photo' },
  { id: 'family-planner', title: 'Family planner', badge: 'Planner' },
];

const screenshotWords = ['screenshot', 'snímek', 'snimek', 'screen', 'obrazovky'];

function getInitialPageId(project: CalendarProject) {
  const queryPageId = new URLSearchParams(window.location.search).get('page');
  return project.pages.some((page) => page.id === queryPageId) ? queryPageId : project.pages[0]?.id;
}

function getPhoto(project: CalendarProject, page?: CalendarPage) {
  return project.photos.find((photo) => photo.id === page?.photoId);
}

function getPhotoIssues(photo?: CalendarPhoto) {
  if (!photo) return [];
  const issues = [...photo.issues];
  const normalizedName = photo.fileName.toLocaleLowerCase('cs-CZ');
  const aspectRatio = photo.width && photo.height ? Math.max(photo.width / photo.height, photo.height / photo.width) : 0;

  if (screenshotWords.some((word) => normalizedName.includes(word)) && !issues.includes('Vypadá jako screenshot – ověř tiskovou kvalitu.')) {
    issues.push('Vypadá jako screenshot – ověř tiskovou kvalitu.');
  }

  if (aspectRatio > 2.5 && !issues.includes('Neobvyklý poměr stran – může být nutný ruční ořez.')) {
    issues.push('Neobvyklý poměr stran – může být nutný ruční ořez.');
  }

  return issues;
}

function getEffectiveStatus(page: CalendarPage, photo?: CalendarPhoto): CalendarPage['preflightStatus'] {
  if (!photo) return 'error';
  if (page.preflightStatus === 'error') return 'error';
  if (photo.score < 75 || getPhotoIssues(photo).length > photo.issues.length || page.preflightStatus === 'warning') return 'warning';
  if (page.preflightStatus === 'ok') return 'ok';
  return 'not_checked';
}

function getRecommendation(page: CalendarPage, photo?: CalendarPhoto) {
  const status = getEffectiveStatus(page, photo);
  if (status === 'ok') return 'Stránka je připravena k další kontrole.';
  if (status === 'warning') return 'Fotka může mít nižší kvalitu, screenshotový původ nebo bude potřebovat ruční ořez.';
  return 'Chybí fotka.';
}

function getPageLabel(page: CalendarPage, index?: number) {
  if (page.type === 'cover') return 'Titulka';
  const pageNumber = typeof index === 'number' ? String(index + 1).padStart(2, '0') : '—';
  return `Stránka ${pageNumber} / ${page.title} ${page.year ?? ''}`;
}

function getMonthNumber(page: CalendarPage) {
  return String(page.monthIndex ?? '').padStart(2, '0');
}

function getImageTransform(page: CalendarPage) {
  const translateX = (0.5 - page.crop.x) * 34;
  const translateY = (0.5 - page.crop.y) * 34;
  return `translate(${translateX}%, ${translateY}%) scale(${page.crop.zoom}) rotate(${page.crop.rotation}deg)`;
}

function PageImage({ project, page, previewUrl }: { project: CalendarProject; page: CalendarPage; previewUrl?: string }) {
  const photo = getPhoto(project, page);

  if (photo && previewUrl) {
    return <img src={previewUrl} alt={photo.fileName} style={{ transform: getImageTransform(page) }} />;
  }

  return (
    <div className="editor-no-preview">
      <strong>Lokální fotka není v tomto prohlížeči dostupná</strong>
      <span>Nahraj fotky znovu nebo pokračuj v exportu metadat.</span>
    </div>
  );
}

function MonthCalendar({ page }: { page: CalendarPage }) {
  const month = page.monthIndex ?? 1;
  const year = page.year ?? new Date().getFullYear();
  const days = getMonthGrid(year, month);

  return (
    <div className="calendar-mini-grid">
      {weekDays.map((day) => <strong key={day}>{day}</strong>)}
      {days.map((day, index) => (
        <span key={`${day.date.toISOString()}-${index}`} className={`${day.isCurrentMonth ? '' : 'is-muted'} ${day.isWeekend ? 'is-weekend' : ''} ${day.isHoliday ? 'is-holiday' : ''}`}>
          {day.day}
        </span>
      ))}
    </div>
  );
}

export function EditorFlow() {
  const [project, setProject] = useState<CalendarProject | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string>('');
  const [showGrid, setShowGrid] = useState(false);
  const [showSafeZone, setShowSafeZone] = useState(true);
  const [showBleed, setShowBleed] = useState(false);
  const [saveState, setSaveState] = useState('Uloženo');
  const previewUrls = usePhotoPreviewUrls(project);

  useEffect(() => {
    const loadedProject = loadLastProject();
    setProject(loadedProject);
    if (loadedProject?.pages.length) {
      setSelectedPageId(getInitialPageId(loadedProject) ?? loadedProject.pages[0].id);
    }
  }, []);

  const selectedPage = useMemo(() => project?.pages.find((page) => page.id === selectedPageId), [project, selectedPageId]);
  const selectedPhoto = project && selectedPage ? getPhoto(project, selectedPage) : undefined;
  const selectedPageIndex = project?.pages.findIndex((page) => page.id === selectedPageId) ?? -1;

  const persistProject = (nextProject: CalendarProject) => {
    const updatedProject = { ...nextProject, updatedAt: new Date().toISOString() };
    setProject(updatedProject);
    saveProject(updatedProject);
    setSaveState('Uloženo');
  };

  const updateSelectedPage = (updater: (page: CalendarPage) => CalendarPage) => {
    if (!project || !selectedPage) return;
    setSaveState('Ukládám…');
    const nextProject = {
      ...project,
      pages: project.pages.map((page) => (page.id === selectedPage.id ? updater(page) : page)),
    };
    persistProject(nextProject);
  };

  const setCropValue = (key: keyof CalendarPage['crop'], value: number) => {
    updateSelectedPage((page) => ({ ...page, crop: { ...page.crop, [key]: value } }));
  };

  const resetCrop = () => {
    updateSelectedPage((page) => ({ ...page, crop: { x: 0.5, y: 0.5, zoom: 1, rotation: 0 } }));
  };

  if (!project) {
    return (
      <main className="editor-shell">
        <section className="editor-empty-state">
          <Logo />
          <h1>Nejdřív vytvoř projekt</h1>
          <p>Editor navazuje na lokální projekt vytvořený ve start flow.</p>
          <Button href="/start">Začít výběrem</Button>
        </section>
      </main>
    );
  }

  if (!project.pages.length) {
    return (
      <main className="editor-shell">
        <section className="editor-empty-state">
          <Logo />
          <h1>Nejdřív vytvoř návrh</h1>
          <p>Editor potřebuje titulku a 12 měsíčních stránek z Magic Draftu.</p>
          <Button href="/draft">Zpět na návrh</Button>
        </section>
      </main>
    );
  }

  if (!selectedPage) return null;

  const selectedPhotoIssues = getPhotoIssues(selectedPhoto);
  const selectedStatus = getEffectiveStatus(selectedPage, selectedPhoto);
  const previewClassName = `editor-page-preview editor-layout-${selectedPage.layoutId} ${showGrid ? 'show-grid' : ''} ${showSafeZone ? 'show-safe-zone' : ''} ${showBleed ? 'show-bleed' : ''}`;

  return (
    <main className="editor-shell">
      <div className="editor-card">
        <header className="editor-header">
          <Logo />
          <div className="editor-header__project">
            <strong>{productLabels[project.productId]}</strong>
            <span>{saveState} · Automaticky uloženo lokálně</span>
          </div>
          <div className="editor-header__actions">
            <Button href="/projects" variant="secondary">Moje projekty</Button>
            <Button href="/draft" variant="secondary">Zpět na návrh</Button>
            <button type="button" onClick={() => persistProject(project)}>Uložit</button>
            <Button href="/preflight">Pokračovat na kontrolu</Button>
          </div>
        </header>

        <nav className="editor-progress" aria-label="Kroky Magic Flow">
          {progressSteps.map((step, index) => <span key={step} className={index === 3 ? 'is-active' : ''}><small>0{index + 1}</small>{step}</span>)}
        </nav>

        <div className="editor-workspace">
          <aside className="editor-sidebar">
            <h2>Stránky</h2>
            <div className="editor-page-list">
              {project.pages.map((page) => {
                const photo = getPhoto(project, page);
                const status = getEffectiveStatus(page, photo);
                return (
                  <button key={page.id} type="button" className={page.id === selectedPage.id ? 'is-active' : ''} onClick={() => setSelectedPageId(page.id)}>
                    <span className="editor-thumb">{photo && previewUrls[photo.id] ? <img src={previewUrls[photo.id]} alt={page.title} /> : <span className="editor-thumb-placeholder">CS</span>}</span>
                    <span><strong>{page.type === 'cover' ? 'Titulka' : page.title}</strong><small className={`editor-status-badge ${statusClassNames[status]}`}>{statusLabels[status]}</small></span>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="editor-canvas" aria-label="Náhled stránky">
            <div className="editor-canvas-label">{getPageLabel(selectedPage, selectedPageIndex)}</div>
            <div className="editor-print-desk">
            <div className={previewClassName}>
              <div className="editor-bleed-overlay" />
              <div className="editor-safe-overlay" />
              <div className="editor-grid-overlay" />

              {selectedPage.type === 'cover' ? (
                <div className="editor-cover-content">
                  <div className="editor-photo-area editor-cover-photo"><PageImage project={project} page={selectedPage} previewUrl={previewUrls[selectedPage.photoId ?? '']} /></div>
                  <div className="editor-cover-title">
                    <span>Calendar Studio</span>
                    <h1>{selectedPage.title}</h1>
                    <p>{selectedPage.subtitle || String(project.period.year)}</p>
                  </div>
                </div>
              ) : (
                <div className="editor-month-content">
                  <div className="editor-photo-area"><PageImage project={project} page={selectedPage} previewUrl={previewUrls[selectedPage.photoId ?? '']} /></div>
                  <div className="editor-calendar-area">
                    <div className="editor-month-title">
                      <span className="editor-month-number">{getMonthNumber(selectedPage)}</span>
                      <div><h1>{selectedPage.title.toLocaleUpperCase('cs-CZ')}</h1>{selectedPage.notes && <p className="editor-caption">{selectedPage.notes}</p>}</div>
                      <strong>{selectedPage.year}</strong>
                    </div>
                    <MonthCalendar page={selectedPage} />
                    {selectedPage.layoutId === 'family-planner' && (
                      <div className="editor-planner-lines" aria-label="Poznámkový blok"><span /><span /><span /></div>
                    )}
                  </div>
                </div>
              )}
            </div>
            </div>
            <footer className="editor-canvas-footer">
              <span>Formát: A3</span><span>Spadávka: 3 mm</span><span>Bezpečná zóna: 5 mm</span><span>Zoom canvasu: 100 %</span>
            </footer>
          </section>

          <aside className="editor-inspector">
            <section>
              <h2>Zobrazení</h2>
              <label><input type="checkbox" checked={showGrid} onChange={(event) => setShowGrid(event.target.checked)} /> Zobrazit mřížku</label>
              <label><input type="checkbox" checked={showSafeZone} onChange={(event) => setShowSafeZone(event.target.checked)} /> Bezpečná zóna</label>
              <label><input type="checkbox" checked={showBleed} onChange={(event) => setShowBleed(event.target.checked)} /> Spadávka</label>
            </section>

            <section>
              <h2>Ořez fotografie</h2>
              <label>Zoom <input type="range" min="1" max="2.5" step="0.01" value={selectedPage.crop.zoom} onChange={(event) => setCropValue('zoom', Number(event.target.value))} /></label>
              <label>Pozice X <input type="range" min="0" max="1" step="0.01" value={selectedPage.crop.x} onChange={(event) => setCropValue('x', Number(event.target.value))} /></label>
              <label>Pozice Y <input type="range" min="0" max="1" step="0.01" value={selectedPage.crop.y} onChange={(event) => setCropValue('y', Number(event.target.value))} /></label>
              <label>Rotace <input type="range" min="-5" max="5" step="0.1" value={selectedPage.crop.rotation} onChange={(event) => setCropValue('rotation', Number(event.target.value))} /></label>
              <button type="button" onClick={resetCrop}>Reset ořezu</button>
            </section>

            <section>
              <h2>Rozložení</h2>
              <div className="layout-options">
                {layoutOptions.map((layout) => (
                  <button key={layout.id} type="button" className={selectedPage.layoutId === layout.id ? 'is-active' : ''} onClick={() => updateSelectedPage((page) => ({ ...page, layoutId: layout.id }))}>
                    {layout.title}{layout.badge && <span>{layout.badge}</span>}
                  </button>
                ))}
              </div>
            </section>

            <section>
              <h2>Texty</h2>
              <input type="text" maxLength={80} placeholder={selectedPage.type === 'cover' ? 'Podtitulek' : 'Popisek fotografie'} value={selectedPage.type === 'cover' ? selectedPage.subtitle ?? '' : selectedPage.notes ?? ''} onChange={(event) => updateSelectedPage((page) => selectedPage.type === 'cover' ? { ...page, subtitle: event.target.value } : { ...page, notes: event.target.value })} />
              <small>{(selectedPage.type === 'cover' ? selectedPage.subtitle?.length : selectedPage.notes?.length) ?? 0}/80 znaků</small>
            </section>

            <section>
              <h2>Kontrola stránky</h2>
              <dl className="editor-checklist">
                <div><dt>Fotka</dt><dd>{selectedPhoto ? 'má fotku' : 'chybí fotka'}</dd></div>
                <div><dt>Rozlišení</dt><dd>{selectedPhoto?.width && selectedPhoto.height ? `${selectedPhoto.width} × ${selectedPhoto.height}` : '—'}</dd></div>
                <div><dt>Score</dt><dd>{selectedPhoto?.score ?? '—'}</dd></div>
                <div><dt>Status</dt><dd>{statusLabels[selectedStatus]}</dd></div>
              </dl>
              {selectedPhotoIssues.length ? <p>{selectedPhotoIssues.join(' · ')}</p> : null}
              <strong>{getRecommendation(selectedPage, selectedPhoto)}</strong>
            </section>
          </aside>
        </div>
      </div>
    </main>
  );
}
